// ============================================================================
// Status und Fehler einer Variante im Varianten-Tab.
//
// Bewusst DOM- und netzfrei, damit die Ableitung testbar ist -- dasselbe Muster wie
// lib/variant-offer-overrides.ts und lib/variant-attribute-selection.ts.
//
// Die Beschriftungen kommen aus `listingPill` (views/shared/listing-detail-pane), NICHT aus
// einer eigenen Liste. Der Varianten-Tab und das Detail-Fenster muessen denselben Zustand
// gleich benennen; zwei Vokabulare fuer dieselbe Sache waeren zwei Wahrheiten. Der Import
// zeigt deshalb ausnahmsweise von lib nach views.
// ============================================================================

import { listingPill, type ListingStatus } from '../views/shared/listing-detail-pane.js';

/** Was die Statuszelle von einer Varianten-Zeile braucht. Alles optional: Eine Zeile ohne
 *  Listing (noch nicht angelegt) oder mit gescheiterter Pruefung traegt nichts davon. */
export interface ZeileFuerStatus {
  status?: string | undefined;
  lastError?: string | null | undefined;
  /** Kauflands Grund waehrend der Pruefung (`kaufland_status_reason`). */
  statusReason?: string | null | undefined;
  /** Der Kaufland-Spiegel, fuer die Unterscheidung "Gelistet" / "Gelistet, ohne Bestand". */
  unit?: { status?: string | null; stock?: number | null } | null | undefined;
}

export interface StatusZelle {
  /** CSS-Klasse der Pille, z.B. `v-pill--ok`. */
  cls: string;
  /** Beschriftung, z.B. "Gelistet" oder "Gelistet, ohne Bestand". */
  label: string;
  /** Fehlertext bzw. Pruefgrund, oder null. */
  hinweis: string | null;
}

/** Zustaende, aus denen sich von selbst nichts mehr aendert. Gleiche Menge wie im
 *  Einzel-Watcher (`watchListingStatus`) -- der Varianten-Tab darf nicht laenger pollen
 *  als das Detail-Fenster fuer dieselbe Zeile. */
const ENDZUSTAENDE = new Set(['published', 'error', 'archived']);

/**
 * Pille und Hinweistext fuer EINE Varianten-Zeile.
 *
 * Der Hinweis haengt am Status, nicht an der blossen Anwesenheit eines Textes: Ein
 * `last_error` wird NUR bei `status = 'error'` gezeigt. Migration 0053 raeumt das Feld beim
 * Klick auf "Listen" zwar auf, aber sollte doch einmal etwas stehen bleiben, darf daneben
 * nicht "Wird gelistet" und gleichzeitig ein Fehler stehen -- genau diese widerspruechliche
 * Anzeige hat den Betreiber am 2026-08-17 eine Runde gekostet.
 *
 * Pure -- keine IO.
 */
export function variantenStatusZelle(zeile: ZeileFuerStatus): StatusZelle {
  if (!zeile.status) return { cls: 'v-pill--muted', label: '—', hinweis: null };

  // `UnitState` verlangt beide Felder; die Zeile liefert sie optional (die Function laesst
  // sie weg, wenn der Kaufland-Spiegel nichts kennt). Auf null normalisieren, statt den
  // Vertrag von UnitState aufzuweichen — dort ist "unbekannt" bewusst `null` und nicht
  // "Feld fehlt".
  const unit = zeile.unit
    ? { status: zeile.unit.status ?? null, stock: zeile.unit.stock ?? null }
    : null;
  const pille = listingPill(zeile.status as ListingStatus, unit);

  let hinweis: string | null = null;
  if (zeile.status === 'error' && zeile.lastError) hinweis = zeile.lastError;
  else if (zeile.status === 'validating' && zeile.statusReason) hinweis = zeile.statusReason;

  return { cls: pille.cls, label: pille.label, hinweis };
}

/**
 * Darf der Poller aufhoeren?
 *
 * Ein unbekannter Status gilt als NICHT fertig: lieber einmal zu oft nachsehen, als eine
 * Zeile fuer erledigt zu erklaeren, die es nicht ist.
 *
 * Pure -- keine IO.
 */
export function alleImEndzustand(stati: readonly (string | undefined | null)[]): boolean {
  return stati.every((s) => typeof s === 'string' && ENDZUSTAENDE.has(s));
}

// ============================================================================
// Poller-Register
//
// Anlass (2026-08-18): `beobachteStatus` startete bei JEDEM Rendern einen Timer und stoppte
// den vorherigen nie. Der Riegel `host.isConnected` greift dafuer nicht -- `renderVariantsTab`
// ersetzt nur `host.innerHTML`, das Host-Element selbst bleibt verbunden. Drei Stellen loesen
// ein Neu-Rendern aus (Verkaufsbedingungen ergaenzen, Varianten anlegen, Aktualisieren), jede
// legte einen weiteren Poller obendrauf. Die alten fragten weiter alle 3 Sekunden die
// Datenbank ab und schrieben in Zellen, die es nicht mehr gab.
//
// Das Detail-Fenster loest dasselbe Problem mit `stopWatchingListing()`. Hier ist das
// Gegenstueck -- bewusst als reines Register ohne Timer-Aufrufe, damit es testbar bleibt:
// Der Aufrufer bekommt die abzuraeumende Timer-ID zurueck und ruft `clearTimeout` selbst.
// ============================================================================

const laufendeTimer = new Map<string, number>();

/**
 * Merkt den Timer einer Familie und gibt den VORHER laufenden zurueck.
 *
 * @returns die alte Timer-ID, die der Aufrufer abraeumen muss, oder null.
 */
export function timerMerken(schluessel: string, timerId: number): number | null {
  const alt = laufendeTimer.get(schluessel) ?? null;
  laufendeTimer.set(schluessel, timerId);
  return alt;
}

/**
 * Vergisst die Familie und gibt ihren laufenden Timer zurueck.
 *
 * @returns die Timer-ID, die der Aufrufer abraeumen muss, oder null.
 */
export function timerVergessen(schluessel: string): number | null {
  const alt = laufendeTimer.get(schluessel) ?? null;
  laufendeTimer.delete(schluessel);
  return alt;
}
